<script setup lang="ts">
import { BookOpen, Loader2 } from 'lucide-vue-next'
import type { CitationSnippet } from '~/components/citation/types'

const { t } = useI18n()

defineProps<{
  label: string
  /** Null until the snippet resolves (or for good, if it never does). */
  snippet: CitationSnippet | null
  /** True while the snippet is still expected to arrive. */
  loading?: boolean
}>()

defineEmits<{ navigate: [] }>()
</script>

<template>
  <div class="w-64 rounded-lg border bg-popover text-popover-foreground shadow-lg">
    <div class="px-3 py-2 border-b">
      <p class="text-xs font-semibold leading-tight">{{ label }}</p>
    </div>

    <!-- Snippet body: loaded snippet / loading spinner / unavailable fallback -->
    <div class="px-3 py-2">
      <p v-if="snippet" class="text-xs text-muted-foreground leading-relaxed line-clamp-4">
        {{ snippet.snippet }}
      </p>
      <p v-else-if="loading" class="flex items-center gap-1.5 text-xs text-muted-foreground italic">
        <Loader2 class="h-3 w-3 animate-spin" />
        {{ t('webChat.citation.loading') }}
      </p>
      <p v-else class="text-xs text-muted-foreground italic">
        {{ t('webChat.citation.fallback') }}
      </p>
    </div>

    <!-- Navigate action -->
    <div class="px-3 py-2 border-t">
      <button
        type="button"
        class="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
        :disabled="!snippet?.sectionId"
        :class="!snippet?.sectionId && 'opacity-50 cursor-not-allowed hover:text-primary'"
        @click="$emit('navigate')"
      >
        <BookOpen class="h-3 w-3" />
        {{ t('webChat.citation.viewFull') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
